import store from 'store'
import { store as reduxStore } from 'index'
import { isGlobal } from 'conf'
import { getPaidLicense } from 'services/landing/utils/api'
import { setPlanContext } from 'utils/plan/tier'

// 같은 순간에 여러 트리거가 겹쳐도 요청은 하나만 나가게 공유하는 in-flight promise.
// 트리거가 7개까지 늘어났고(거부 응답 인터셉터·저장 성공·로컬 차단·삭제 2곳·소유권 이전·
// 목록 새로고침) 조율 장치가 없어서, 한 동작이 여러 트리거를 동시에 때리거나 사용자가
// 연타하면 그만큼 요청이 나갔다. 이 엔드포인트는 서버 캐시가 없고 매 요청 첨부 합계를
// SUM 으로 재계산한다 — 가져오기 500건이면 거부 500회 = 조회 500회였다 (2026-08-26).
let inFlight = null
// 직전 성공 시각. 짧은 간격의 재요청은 방금 받은 값으로 답한다(연타·연쇄 트리거 흡수).
let lastSuccessAt = 0
const MIN_INTERVAL_MS = 1500

/** 테스트에서 모듈 상태를 초기화한다 (single-flight·쓰로틀은 모듈 스코프 상태다). */
export const resetReloadLicenseState = () => {
  inFlight = null
  lastSuccessAt = 0
}

const currentWorkspaceId = () => {
  try {
    return store.get('app.user.workspace_id')
  } catch (e) {
    return null
  }
}

const hasToken = () => {
  try {
    return Boolean(store.get('app.user.token'))
  } catch (e) {
    return false
  }
}

const fetchAndApply = async () => {
  // 요청 시점의 워크스페이스를 스냅샷으로 잡는다 — 헤더는 전송 시점 localStorage 를
  // 읽으므로, 전환 전에 발사된 응답이 늦게 도착하면 **새 워크스페이스 화면에 옛
  // 워크스페이스 라이선스**가 심긴다(등급·용량이 통째로 다른 값). 응답 도착 시 비교해
  // 달라졌으면 버린다.
  const requestedWorkspaceId = currentWorkspaceId()

  let license = null
  try {
    license = await getPaidLicense()
  } catch (e) {
    return null
  }
  if (license?.code !== 200) return null

  // 로그아웃·워크스페이스 전환이 응답을 앞질렀으면 심지 않는다 — 로그아웃 saga 가
  // setPlanContext(null) 을 한 뒤에 이 응답이 컨텍스트를 되살리면 안 된다.
  if (!hasToken() || currentWorkspaceId() !== requestedWorkspaceId) return null

  const paidLicense = license.data
  lastSuccessAt = Date.now()
  reduxStore.dispatch({ type: 'user/SET_STATE', payload: { paidLicense } })
  setPlanContext({ isGlobal: isGlobal(), license: paidLicense })
  // 메뉴는 tier 로 항목이 갈리므로(인증 코드 탭 등) 재빌드해야 반영된다
  reduxStore.dispatch({ type: 'menu/RELOAD' })
  return paidLicense
}

/**
 * 라이선스를 재조회해 redux·게이팅 컨텍스트·메뉴에 반영하고 새 license 를 돌려준다.
 *
 * **모든 재조회 트리거는 이 함수를 거친다** — saga(`user/RELOAD_PAID_LICENSE`)도 이걸
 * 호출한다. 구현이 둘로 갈려 있으면 single-flight·쓰로틀·가드를 한쪽만 갖게 되고,
 * 실제로 그 상태에서 요청 폭주와 워크스페이스 교차 오염이 났다(2026-08-26).
 *
 * 계약:
 * - **single-flight**: 진행 중 요청이 있으면 그 promise 를 공유한다(중복 발사 없음)
 * - **쓰로틀**: 직전 성공 후 1.5초 안의 요청은 네트워크 없이 현재 값을 돌려준다
 * - 비-200 응답이면 아무것도 덮어쓰지 않는다 (stale 유지가 null 보다 안전)
 * - 실패를 삼킨다 (갱신 실패가 저장 같은 원래 흐름을 깨면 안 된다)
 * - 토큰 없음(로그아웃)·워크스페이스 전환 후 도착한 응답은 버린다
 * - tier 로 항목이 갈리는 메뉴(인증 코드 탭)는 재빌드한다
 *
 * @returns {Promise<object|null>} 갱신된 license data, 실패·비200·건너뜀이면 null
 */
export const reloadPlanLicense = async () => {
  if (!hasToken()) return null

  if (inFlight) return inFlight

  if (lastSuccessAt && Date.now() - lastSuccessAt < MIN_INTERVAL_MS) {
    // 방금 갱신했다 — 같은 클릭 안에서 재판정하는 호출부(RecordEdit)도 최신값을 본다
    return reduxStore.getState()?.user?.paidLicense ?? null
  }

  inFlight = fetchAndApply().finally(() => {
    inFlight = null
  })
  return inFlight
}

export default reloadPlanLicense
